import { QUESTION_PERMISSIONS } from '@hubertai/screening-question-types';
import type { AgPermission } from '@hubertai/screening-question-types';
import { CREATE_MODES, ECreateQuestionMode } from '../context/create-question.context';
import type { TVisibility } from 'types/template/template.types';
import type { TRecruiterAppUser } from 'types/user.types';

export type TRecruiterScopeContext = {
  mode: ECreateQuestionMode;
  visibility: TVisibility | null | undefined;
  clientId: string | undefined;
  accessGroupIds: string[] | undefined;
  hasQuestionUpdatePermission: boolean;
  templateReferenceId: string | undefined;
  templateVisibility: TVisibility | null | undefined;
};

export type TAccessGroupChoice = { id: string; name: string };

export type TRecruiterScopeCorrections = {
  visibility?: TVisibility;
  clientId?: string;
  accessGroupIds?: string[];
};

const isCreating = (context: TRecruiterScopeContext) => CREATE_MODES.includes(context.mode);

const keepsTemplateAccessGroups = (context: TRecruiterScopeContext) =>
  [ECreateQuestionMode.InTemplateCreate, ECreateQuestionMode.InTemplateCopy].includes(context.mode) &&
  context.templateVisibility === 'local';

const groupIdsWith = (user: TRecruiterAppUser, permission: AgPermission): string[] =>
  (user.access_groups_permissions ?? [])
    .filter((ag) => ag.permissions.includes(permission))
    .map((ag) => ag.accessGroupId);

const memberGroupIds = (user: TRecruiterAppUser): string[] =>
  (user.access_groups_permissions ?? []).map((ag) => ag.accessGroupId);

export const canChangeOrganizationLevel = (user: TRecruiterAppUser): boolean =>
  (user.access_groups_permissions ?? []).some(
    (ag) =>
      ag.permissions.includes(QUESTION_PERMISSIONS.ManageClientLevel) ||
      (ag.permissions.includes(QUESTION_PERMISSIONS.Update) && ag.permissions.includes(QUESTION_PERMISSIONS.ShareOrg)),
  );

/** A super admin may put a question in any of their client's groups, not just their own. */
export const picksAnyClientGroup = (user: TRecruiterAppUser): boolean =>
  (user.access_groups_permissions ?? []).some((ag) => ag.permissions.includes(QUESTION_PERMISSIONS.ManageClientLevel));

export const canSeeRecruiterScopePicker = (user: TRecruiterAppUser, context: TRecruiterScopeContext): boolean => {
  if (isCreating(context)) {
    return groupIdsWith(user, QUESTION_PERMISSIONS.Create).length > 0 || canChangeOrganizationLevel(user);
  }
  return context.hasQuestionUpdatePermission;
};

/** Every group the recruiter is a member of, with any role. */
export const getRecruiterScopeOptions = (user: TRecruiterAppUser): TAccessGroupChoice[] =>
  (user.access_groups_permissions ?? []).map((ag) => ({ id: ag.accessGroupId, name: ag.accessGroupName }));

/**
 * The question's groups the recruiter is not a member of. They can't be removed, so the picker keeps
 * them and only counts them, since the recruiter has no names for them.
 */
export const getOtherAccessGroupIds = (user: TRecruiterAppUser, accessGroupIds: string[] | undefined): string[] => {
  const memberIds = memberGroupIds(user);
  return (accessGroupIds ?? []).filter((id) => !memberIds.includes(id));
};

export const applyMemberGroupChoice = (
  user: TRecruiterAppUser,
  currentAccessGroupIds: string[] | undefined,
  chosenMemberGroupIds: string[],
): string[] => [...chosenMemberGroupIds, ...getOtherAccessGroupIds(user, currentAccessGroupIds)];

/**
 * Whether a change of groups takes away the recruiter's last group with `question:update` on the
 * question, so they couldn't edit it after saving. Allowed — it hands the question over — but worth
 * a confirmation.
 */
export const losesQuestionUpdate = (
  user: TRecruiterAppUser,
  beforeAccessGroupIds: string[] | undefined,
  afterAccessGroupIds: string[],
): boolean => {
  const updateGroupIds = groupIdsWith(user, QUESTION_PERMISSIONS.Update);
  const canUpdateBefore = (beforeAccessGroupIds ?? []).some((id) => updateGroupIds.includes(id));
  const canUpdateAfter = afterAccessGroupIds.some((id) => updateGroupIds.includes(id));
  return canUpdateBefore && !canUpdateAfter;
};

/** A new local question needs `question:create` in at least one of its groups. */
export const chosenGroupsLackQuestionCreate = (user: TRecruiterAppUser, context: TRecruiterScopeContext): boolean => {
  const chosen = context.accessGroupIds ?? [];
  if (!isCreating(context) || context.visibility !== 'local' || !chosen.length || picksAnyClientGroup(user)) {
    return false;
  }

  const createGroupIds = groupIdsWith(user, QUESTION_PERMISSIONS.Create);
  return !chosen.some((id) => createGroupIds.includes(id));
};

/**
 * What a new question's scope has to become before the recruiter can save it: local unless they may
 * create it at Organization level, their own client, and only groups they are a member of (any of the
 * client's groups for a super admin) — with their only create group picked for them when nothing is left. A question being edited is never
 * corrected; its scope is the recruiter's to change through the picker.
 */
export const getRecruiterScopeCorrections = (
  user: TRecruiterAppUser,
  context: TRecruiterScopeContext,
): TRecruiterScopeCorrections => {
  if (!isCreating(context) || !canSeeRecruiterScopePicker(user, context)) return {};

  const corrections: TRecruiterScopeCorrections = {};
  if (context.visibility !== 'local' && !canChangeOrganizationLevel(user)) corrections.visibility = 'local';
  if (context.clientId !== user.client_id) corrections.clientId = user.client_id;

  const endsUpLocal = (corrections.visibility ?? context.visibility) === 'local';
  if (!endsUpLocal || keepsTemplateAccessGroups(context) || picksAnyClientGroup(user)) return corrections;

  const current = context.accessGroupIds ?? [];
  const memberIds = memberGroupIds(user);
  const narrowed = current.filter((id) => memberIds.includes(id));
  const createGroupIds = groupIdsWith(user, QUESTION_PERMISSIONS.Create);
  const groups = !narrowed.length && createGroupIds.length === 1 ? createGroupIds : narrowed;

  const changed = groups.length !== current.length || groups.some((id, index) => id !== current[index]);
  if (changed) corrections.accessGroupIds = groups;
  return corrections;
};
